/// <reference types="vite/client" />
import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';
import rateLimit from 'axios-rate-limit';
import { handleError, parseAxiosError, AppError, ErrorCode } from '../../utils/errorHandler.js';
import { csrfProtectionService } from './csrfProtectionService.js';
import { jwtValidationService, JWTValidationErrorCode } from './jwtValidationService.js';
import { secureTokenService } from './secureTokenService.js';
import { validateApiUrl, sanitizeApiUrl } from './apiUrlValidator';

// Development flag for mock authentication
const USEMOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

/**
 * Detects tenant ID from hostname for multi-tenant architecture
 * @returns Tenant ID extracted from subdomain or default 'brain'
 */
const detectTenantFromHostname = (): string => {
  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];
  
  // Handle development and production scenarios
  if (subdomain === 'dev-brain' || subdomain === 'demo-brain') {
    return 'brain';
  }
  
  // For tenant-specific subdomains (e.g., tenant1-brain.astrapeak.ai)
  if (subdomain.includes('-brain')) {
    return subdomain.replace('-brain', '');
  }
  
  // Handle 'current' fallback - map to 'brain' for dev-brain domain
  if (subdomain === 'current') {
    return 'brain';
  }
  
  // Default fallback for localhost and other scenarios
  return subdomain === 'localhost' || subdomain === '127' ? 'brain' : subdomain;
};

/**
 * Creates a configured API client for making requests to the BRAIN backend
 * @param baseURL - Optional base URL override
 * @returns Axios instance configured with auth token and tenant headers
 */
// Import the tenant configuration system
import tenantConfig, { getBaseUrl } from '../utils/apiConfig';

export const createApiClient = (baseURL?: string): AxiosInstance => {
  // Determine base URL
  let brainBaseURL = '';
  
  // If baseURL is provided, use it directly without validation
  // This is a critical change to prevent fallback to hardcoded URLs
  if (baseURL) {
    console.log(`Using provided API URL: ${baseURL}`);
    brainBaseURL = baseURL;
  } else {
    // Use the tenant configuration system to get the base URL
    // This ensures consistent tenant context across the application
    brainBaseURL = getBaseUrl();
    console.log(`Using tenant API URL from configuration: ${brainBaseURL}`);
  }
  
  // Create API client
  // CRITICAL FIX: Never add /api suffix - endpoints should include full path
  // This prevents double /api/api/ in URLs
  const axiosInstance = axios.create({
    baseURL: brainBaseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  // SECURITY: Apply rate limiting to prevent DoS attacks
  // Limit to 10 requests per second (600 requests per minute)
  // This prevents frontend from overwhelming backend and causing AWS cost spikes
  const apiClient = rateLimit(axiosInstance, { 
    maxRequests: 10, 
    perMilliseconds: 1000,
    maxRPS: 10 
  });
  
  // Add request interceptor to include auth token, tenant ID, and CSRF token
  apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      // Get auth token from secure token service (with fallback to localStorage)
      const token = secureTokenService.getAuthToken();
      const storedTenantId = secureTokenService.getTenantId();
      
      // Add Authorization header if token exists
      if (token) {
        // BACKWARD COMPATIBLE: Validate token before using it
        // Only validates expiration; other checks require backend changes (JWKS, iss/aud standardization)
        const validationResult = jwtValidationService.validateToken(token, {
          validateExpiration: true,
          validateSignature: false, // Requires JWKS endpoint and jose library (tracked in APB backlog)
          validateIssuer: false,    // Backend does not consistently set issuer claim yet
          validateAudience: false   // Backend does not consistently set audience claim yet
        });
        
        if (validationResult.valid) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          const isExpired = validationResult.errors?.some(
            e => e.code === JWTValidationErrorCode.EXPIRED_TOKEN
          );
          if (isExpired) {
            // Expired tokens must not be sent — clear auth and let the 401 interceptor handle redirect
            console.warn('Token expired, clearing auth data');
            secureTokenService.clearAuthData();
          } else {
            // Non-expiration validation failures: attach token for backward compatibility
            // Strict mode will be enabled after backend token standardization (APB backlog)
            console.warn('Token validation warning (non-blocking):', validationResult.errors);
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
      }
      
      // Enhanced tenant detection: hostname-based with secure storage fallback
      const detectedTenantId = detectTenantFromHostname();
      const finalTenantId = storedTenantId || detectedTenantId;
      
      // Add X-Tenant-ID header for multi-tenant architecture
      if (finalTenantId) {
        config.headers['X-Tenant-ID'] = finalTenantId;
      }
      
      // Store detected tenant ID for consistency (if not already stored)
      if (!storedTenantId && detectedTenantId) {
        secureTokenService.storeTenantId(detectedTenantId);
      }
      
      // Add CSRF token to non-GET requests
      if (config.method && config.method.toUpperCase() !== 'GET') {
        // Get or initialize CSRF token
        let csrfToken = csrfProtectionService.getStoredCSRFToken();
        if (!csrfToken) {
          csrfToken = csrfProtectionService.initializeCSRFProtection();
        }
        
        // Add CSRF token to headers
        if (csrfToken) {
          config.headers.set('X-XSRF-TOKEN', csrfToken);
        }
      }
      
      return config;
    },
    (error) => {
      // Handle request setup errors
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );
  
  // Track if we're currently refreshing to prevent multiple simultaneous refresh attempts
  let isRefreshing = false;
  let failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    
    failedQueue = [];
  };

  // Add response interceptor to handle auth errors, refresh tokens, and standardize error responses
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      // Extract and process warning headers if present
      const warningHeaders = extractWarningHeaders(response);
      if (warningHeaders.length > 0) {
        // Attach warnings to the response object for processing by service layer
        response.data = response.data || {};
        response.data._warnings = warningHeaders;
      }
      
      // Check for CSRF token in response headers and update if present
      const csrfToken = response.headers['x-csrf-token'] || response.headers['X-CSRF-TOKEN'];
      if (csrfToken) {
        csrfProtectionService.storeCSRFToken(csrfToken);
      }
      
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as any;
      
      // Parse the error into our standardized format
      const appError = parseAxiosError(error);
      
      // Handle authentication errors with token refresh
      if ((appError.code === ErrorCode.AUTHENTICATION_ERROR || 
           appError.code === ErrorCode.TOKEN_EXPIRED ||
           error.response?.status === 401) && 
          !originalRequest._retry) {
        
        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return apiClient(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = secureTokenService.getRefreshToken();
        
        if (!refreshToken) {
          // No refresh token available, redirect to login
          console.error('Authentication failed: No refresh token available for token renewal');
          console.error('User will be redirected to login page');
          secureTokenService.clearAuthData();
          
          // Add a small delay to allow console logs to be visible
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
          
          return Promise.reject(appError);
        }

        try {
          // Get tenant info for the refresh request
          const userData = secureTokenService.getUserData();
          const tenantId = userData?.tenant_id || secureTokenService.getTenantId();
          
          // Call refresh token endpoint
          const response = await axios.post(`${getBaseUrl()}/api/auth/refresh`, {
            refresh_token: refreshToken,
            grant_type: 'refresh_token',
            scope: tenantId ? `tenant_id:${tenantId}` : undefined
          });

          const { access_token, refresh_token: newRefreshToken } = response.data;
          
          // Store new tokens
          secureTokenService.storeAuthToken(access_token);
          if (newRefreshToken) {
            secureTokenService.storeRefreshToken(newRefreshToken);
          }
          
          // Update the authorization header
          originalRequest.headers['Authorization'] = 'Bearer ' + access_token;
          
          // Process queued requests
          processQueue(null, access_token);
          
          isRefreshing = false;
          
          // Retry the original request
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear auth and redirect to login
          processQueue(refreshError, null);
          isRefreshing = false;
          secureTokenService.clearAuthData();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
      
      // Log the standardized error
      handleError(appError);
      
      return Promise.reject(appError);
    }
  );
  
  /**
   * Extract warning headers from response
   * Checks multiple possible header names for warnings
   */
  function extractWarningHeaders(response: AxiosResponse): any[] {
    const warningHeaderNames = [
      'x-validation-warnings',
      'X-Validation-Warnings',
      'validation-warnings',
      'warning'
    ];
    
    let warnings: any[] = [];
    
    // Check each possible header name
    for (const headerName of warningHeaderNames) {
      const warningHeader = response.headers[headerName];
      if (warningHeader) {
        try {
          // Try to parse as JSON
          const parsedWarnings = JSON.parse(warningHeader);
          warnings = Array.isArray(parsedWarnings) ? parsedWarnings : [parsedWarnings];
        } catch (e) {
          // If not valid JSON, use as string
          warnings = [{ message: warningHeader }];
        }
        break;
      }
    }
    
    return warnings;
  }
  
  return apiClient;
};

// Singleton instance — created once at module load.
// All callers share the same instance, interceptors, isRefreshing flag, and failedQueue.
// This prevents multiple simultaneous token refresh calls when concurrent requests hit 401.
const defaultApiClient = createApiClient();

/**
 * Enhanced API client with retry capability for transient failures
 */
export const apiClientWithRetry = {
  async request<T = any>(config: AxiosRequestConfig, retries = 2, retryDelay = 1000): Promise<AxiosResponse<T>> {
    try {
      return await defaultApiClient.request(config) as AxiosResponse<T>;
    } catch (error) {
      const appError = error as AppError;
      
      // Only retry for network errors, timeouts, and 5xx server errors
      const isRetryable = (
        appError.code === ErrorCode.NETWORK_ERROR ||
        appError.code === ErrorCode.TIMEOUT_ERROR ||
        appError.code === ErrorCode.API_ERROR ||
        (appError.httpStatus && appError.httpStatus >= 500 && appError.httpStatus < 600)
      );
      
      if (isRetryable && retries > 0) {
        console.log(`Retrying request (${retries} attempts left)...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.request(config, retries - 1, retryDelay * 2) as Promise<AxiosResponse<T>>;
      }
      
      throw appError;
    }
  },
  
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request({ ...config, url, method: 'GET' }) as Promise<AxiosResponse<T>>;
  },
  
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request({ ...config, url, method: 'POST', data }) as Promise<AxiosResponse<T>>;
  },
  
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request({ ...config, url, method: 'PUT', data }) as Promise<AxiosResponse<T>>;
  },
  
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request({ ...config, url, method: 'DELETE' }) as Promise<AxiosResponse<T>>;
  },
  
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request({ ...config, url, method: 'PATCH', data }) as Promise<AxiosResponse<T>>;
  }
};

export default defaultApiClient;
